/**
 * Network utilities for handling timeouts, retries, and connection issues
 */

/**
 * Enhanced fetch with timeout support
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 30000ms)
 * @returns {Promise<Response>} Fetch response
 */
export const fetchWithTimeout = (url, options = {}, timeout = 30000) => {
    // Create abort controller for timeout
    const controller = new AbortController();
    const signal = controller.signal;

    // Set up timeout
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeout);

    // Merge abort signal with options
    const fetchOptions = {
        ...options,
        signal: signal
    };

    return fetch(url, fetchOptions)
        .then(response => {
            clearTimeout(timeoutId);
            return response;
        })
        .catch(error => {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                const timeoutError = new Error(`Request timeout after ${timeout}ms`);
                timeoutError.code = 'REQUEST_TIMEOUT';
                timeoutError.timeout = timeout;
                throw timeoutError;
            }
            
            throw error;
        });
};

/**
 * Retry configuration for different error types
 */
export const RETRY_CONFIG = {
    // Network/connection errors - retry with backoff
    CONNECTION_ERROR: {
        maxRetries: 3,
        baseDelay: 1000,
        maxDelay: 8000,
        backoffFactor: 2
    },
    
    // Request timeout - retry with shorter delay
    REQUEST_TIMEOUT: {
        maxRetries: 2,
        baseDelay: 500,
        maxDelay: 2000,
        backoffFactor: 1.5
    },
    
    // Server errors (5xx) - retry with moderate delay
    SERVER_ERROR: {
        maxRetries: 2,
        baseDelay: 2000,
        maxDelay: 6000,
        backoffFactor: 2
    },
    
    // Rate limiting (429) - retry with longer delay
    RATE_LIMIT: {
        maxRetries: 3,
        baseDelay: 5000,
        maxDelay: 15000,
        backoffFactor: 2
    }
};

/**
 * Determine if an error is retryable
 * @param {Error} error - Error to check
 * @param {number} statusCode - HTTP status code if available
 * @returns {string|null} Retry config key or null if not retryable
 */
export const getRetryConfigForError = (error, statusCode) => {
    // Connection/network errors
    if (error.message === 'Failed to fetch' || 
        error.code === 'CONNECTION_ERROR' ||
        error.message?.includes('net::ERR_CONNECTION_REFUSED') ||
        error.message?.includes('net::ERR_NETWORK_CHANGED')) {
        return 'CONNECTION_ERROR';
    }
    
    // Request timeout
    if (error.code === 'REQUEST_TIMEOUT' || error.name === 'AbortError') {
        return 'REQUEST_TIMEOUT';
    }
    
    // HTTP status code based retries
    if (statusCode) {
        if (statusCode === 429) {
            return 'RATE_LIMIT';
        }
        
        if (statusCode >= 500 && statusCode < 600) {
            return 'SERVER_ERROR';
        }
    }
    
    return null; // Not retryable
};

/**
 * Calculate delay for retry attempt with exponential backoff
 * @param {Object} config - Retry configuration
 * @param {number} attemptNumber - Current attempt number (0-based)
 * @returns {number} Delay in milliseconds
 */
export const calculateRetryDelay = (config, attemptNumber) => {
    const delay = config.baseDelay * Math.pow(config.backoffFactor, attemptNumber);
    return Math.min(delay, config.maxDelay);
};

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Enhanced API request with timeout and retry logic
 * @param {Function} apiRequestFn - Original API request function
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {*} body - Request body
 * @param {Object} customConfig - Custom configuration
 * @param {boolean} returnFullResponse - Whether to return full response
 * @param {Object} options - Additional options
 * @param {number} options.timeout - Request timeout in milliseconds
 * @param {boolean} options.enableRetry - Whether to enable automatic retries
 * @param {Function} options.onRetry - Callback when retry occurs
 * @returns {Promise<*>} API response
 */
export const apiRequestWithRetry = async (
    apiRequestFn,
    endpoint,
    method = 'GET',
    body = null,
    customConfig = {},
    returnFullResponse = false,
    options = {}
) => {
    const {
        timeout = 30000,
        enableRetry = true,
        onRetry = null
    } = options;

    let attemptNumber = 0;

    while (true) {
        try {
            // Add timeout to custom config
            const configWithTimeout = {
                ...customConfig,
                timeout
            };

            const result = await apiRequestFn(endpoint, method, body, configWithTimeout, returnFullResponse);
            return result;

        } catch (error) {
            if (!enableRetry) {
                throw error;
            }

            const statusCode = error.status || error.meta?.http_status_code;
            const retryConfigKey = getRetryConfigForError(error, statusCode);
            
            if (!retryConfigKey) {
                // Error is not retryable
                throw error;
            }

            const retryConfig = RETRY_CONFIG[retryConfigKey];
            
            if (attemptNumber >= retryConfig.maxRetries) {
                // Max retries exceeded
                const maxRetriesError = new Error(`Max retry attempts (${retryConfig.maxRetries}) exceeded for ${endpoint}`);
                maxRetriesError.originalError = error;
                maxRetriesError.totalAttempts = attemptNumber + 1;
                maxRetriesError.code = 'MAX_RETRIES_EXCEEDED';
                throw maxRetriesError;
            }

            const delay = calculateRetryDelay(retryConfig, attemptNumber);
            
            // Call retry callback if provided
            if (onRetry) {
                onRetry({
                    error,
                    attemptNumber: attemptNumber + 1,
                    maxRetries: retryConfig.maxRetries,
                    delay,
                    endpoint,
                    method
                });
            }

            await sleep(delay);
            attemptNumber++;
        }
    }
};

/**
 * Check if the network is available
 * @returns {boolean} Network availability
 */
export const isNetworkAvailable = () => {
    return navigator.onLine;
};

/**
 * Monitor network connectivity changes
 * @param {Function} onOnline - Callback when network comes online
 * @param {Function} onOffline - Callback when network goes offline
 * @returns {Function} Cleanup function
 */
export const monitorNetworkStatus = (onOnline, onOffline) => {
    const handleOnline = () => onOnline && onOnline();
    const handleOffline = () => onOffline && onOffline();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
    };
};
